import {Alert, Button, Col, Drawer, Modal, Row, Space, Spin} from "antd";
import {StyledInfoItem} from "../../../customers/details-page/info-section/info-section.style";
import {
  useGetAttendeeDetails,
  AttendeeAnalytics,
} from "../../../../features/live-event-analytics";
import {StyledModalContentWrapper} from "../../../calls/upcoming-calls/call-card/call-card.style";
import {StyledSessionDetailsDrawer} from "../../../calls/service/sales-and-insights/customers-section/style";
import {CloseOutlined} from "@ant-design/icons";
import {SessionDetailsDrawerBookingDetails} from "../../../calls/service/sales-and-insights/customers-section/booking-details";
import {SessionDetailsDrawerBookingQuestions} from "../../../calls/service/sales-and-insights/customers-section/booking-questions";

type TAttendeeDetailsModalProps = {
  eventId?: string;
  attendeeId?: string | number;
  attendeeEmail?: string;
  inlineAttendee?: AttendeeAnalytics;
  visibleState: boolean;
  okFunc: () => void;
  cancelFunc: () => void;
  footerContent?: React.ReactNode[];
};

export function AttendeesDetailsModal({
  eventId,
  attendeeId,
  attendeeEmail,
  inlineAttendee,
  cancelFunc,
  okFunc,
  visibleState,
  footerContent,
}: TAttendeeDetailsModalProps) {
  const attendeeDetailsQuery = useGetAttendeeDetails({
    eventId,
    attendeeId,
    attendeeEmail,
    inlineAttendee,
  });

  const details = attendeeDetailsQuery.data;

  if (attendeeDetailsQuery.isLoading) {
    return (
      <Drawer
        title="Booking details"
        placement="right"
        onClose={cancelFunc}
        visible={visibleState}
        closable={false}
        extra={
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={cancelFunc}
          ></Button>
        }
      >
        <StyledModalContentWrapper>
          <div
            style={{
              height: "30vh",
              display: "flex",
              alignItems: "center",
              justifyContent: " center",
            }}
          >
            <Spin size="default" />
          </div>
        </StyledModalContentWrapper>
      </Drawer>
    );
  }

  if (attendeeDetailsQuery.isError) {
    return (
      <Drawer
        title="Booking details"
        placement="right"
        onClose={cancelFunc}
        visible={visibleState}
        closable={false}
        extra={
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={cancelFunc}
          ></Button>
        }
      >
        <StyledModalContentWrapper>
          <Alert
            message="Error"
            description={attendeeDetailsQuery.error.message}
            type="error"
            showIcon
          />
        </StyledModalContentWrapper>
      </Drawer>
    );
  }

  if (!details) {
    return null;
  }

  const displayPrice = details.getDisplayPrice();

  return (
    <>
      <StyledSessionDetailsDrawer
        title="Booking details"
        placement="right"
        onClose={cancelFunc}
        visible={visibleState}
        closable={false}
        extra={
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={cancelFunc}
          ></Button>
        }
      >
        <div>
          <Space direction="vertical" style={{display: "flex"}} size={24}>
            <SessionDetailsDrawerBookingDetails
              data={[
                {
                  title: "Customer's name",
                  value: details.fullName,
                },
                {
                  title: "Customer's email",
                  value: details.emailAddress,
                },
                {
                  title: "Date",
                  value: details.createdAt.toDateString(),
                },
                ...(details.hasPurchaseHistory()
                  ? [
                      {
                        title: "No of purchases",
                        value: String(details.totalPurchases),
                      },
                      {
                        title: "Total volume",
                        value: String(details.totalVolume),
                      },
                    ]
                  : []),
                {
                  title: displayPrice.amount === 0 ? "Price" : "Amount",
                  value:
                    displayPrice.amount === 0
                      ? "Free"
                      : `${displayPrice.currency} ${displayPrice.amount}`,
                },
                {title: "Reference", value: details.reference},
              ]}
            />
            {details.hasBookingQuestions() && (
              <SessionDetailsDrawerBookingQuestions
                data={details.bookingQuestions.map((q) => ({
                  question: q.question,
                  answer: q.answer ?? undefined,
                }))}
              />
            )}
          </Space>
        </div>
      </StyledSessionDetailsDrawer>
      <Modal
        title="Details Modal"
        onOk={okFunc}
        onCancel={cancelFunc}
        footer={footerContent}
      >
        <Spin spinning={attendeeDetailsQuery.isFetching}>
          <StyledModalContentWrapper>
            <Row gutter={[32, 20]}>
              <Col span={24} md={12}>
                <StyledInfoItem>
                  <p className="info-title">Name</p>
                  <p className="info-value">{details.fullName}</p>
                </StyledInfoItem>
              </Col>
              <Col span={24} md={12}>
                <StyledInfoItem>
                  <p className="info-title">Email</p>
                  <p className="info-value">{details.emailAddress}</p>
                </StyledInfoItem>
              </Col>
              {details.hasPurchaseHistory() && (
                <>
                  <Col span={24} md={12}>
                    <StyledInfoItem>
                      <p className="info-title">Number of purchases</p>
                      <p className="info-value">{details.totalPurchases}</p>
                    </StyledInfoItem>
                  </Col>
                  <Col span={24} md={12}>
                    <StyledInfoItem>
                      <p className="info-title">Total volume</p>
                      <p className="info-value">
                        {details.totalVolume?.toLocaleString("en-US")}
                      </p>
                    </StyledInfoItem>
                  </Col>
                </>
              )}
              {details.hasPhoneNumber() && (
                <Col span={24} md={12}>
                  <StyledInfoItem>
                    <p className="info-title">Phone number</p>
                    <p className="info-value">{details.phoneNumber}</p>
                  </StyledInfoItem>
                </Col>
              )}
            </Row>
          </StyledModalContentWrapper>
        </Spin>
      </Modal>
    </>
  );
}
